import { useEffect, useState } from "react";
import { Alert } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ModalAtom, SegmentedToggleAtom } from "../../../components/atoms/atom";
import PermissionPresetSelector from "../../../components/project/PermissionPresetSelector";
import {
	isTradeAccessIncomplete,
	normalizePermissions,
} from "../../../constant/projectPermissions";
import { useInviteProvisionalMemberMutation } from "../../../store/services/projectMembers";
import isGlobalAdminRole from "../../../utils/isGlobalAdmin";
import { showToast } from "../../../utils/method";
import PermissionsForm from "../_shared/permissions-form";

function InviteProvisionalModal({ show, onClose, member, projectId, onSuccess }) {
	const { t } = useTranslation();
	const globalRole = useSelector((state) => state.auth?.user?.role);
	const isGlobalAdmin = isGlobalAdminRole(globalRole);

	const [permissions, setPermissions] = useState(null);
	const [presetUsedName, setPresetUsedName] = useState(null);
	const [isProjectAdmin, setIsProjectAdmin] = useState(false);

	const [inviteProvisional, { isLoading }] = useInviteProvisionalMemberMutation();

	useEffect(() => {
		if (show && member) {
			setPermissions(normalizePermissions(null));
			setPresetUsedName(null);
			setIsProjectAdmin(false);
		}
	}, [show, member]);

	const handleSave = async () => {
		if (!member?.id) return;
		try {
			await inviteProvisional({
				projectId,
				memberId: member.id,
				// In Admin mode the backend stores no blob; we still send one to
				// satisfy validation.
				permissions: normalizePermissions(permissions),
				presetUsedName,
				...(isGlobalAdmin ? { isProjectAdmin } : {}),
			}).unwrap();
			showToast(t("projects.members.invitations-sent"), { type: "success" });
			onSuccess?.();
			onClose();
		} catch (error) {
			showToast(error?.data?.message || t("errors.general"), { type: "error" });
		}
	};

	return (
		<ModalAtom
			show={show}
			handleClose={onClose}
			size="lg"
			title={t("projects.members.invite-to-project")}
			saveText={isLoading ? "common.saving" : "projects.members.send-invite"}
			disabled={isLoading || !permissions}
			submitDisabled={isTradeAccessIncomplete(permissions)}
			handleSubmit={handleSave}
			body={
				permissions && (
					<>
						{isGlobalAdmin && (
							<SegmentedToggleAtom
								className="mb-3"
								ariaLabel={t("projects.members.invite-to-project")}
								value={isProjectAdmin ? "admin" : "custom"}
								onChange={(mode) =>
									setIsProjectAdmin(mode === "admin")
								}
								options={[
									{
										label: t(
											"projects.permissions.permission-mode-admin",
										),
										value: "admin",
									},
									{
										label: t(
											"projects.permissions.permission-mode-custom",
										),
										value: "custom",
									},
								]}
							/>
						)}

						{isProjectAdmin ? (
							<Alert variant="info" className="mb-0">
								{t("projects.permissions.admin-full-access")}
							</Alert>
						) : (
							<>
								<PermissionPresetSelector
									projectId={projectId}
									appliedPermissions={permissions}
									onPresetApplied={({ presetUsedName: name, permissions: perms }) => {
										setPresetUsedName(name);
										setPermissions(perms);
									}}
								/>
								<PermissionsForm
									projectId={projectId}
									permissions={permissions}
									onChange={setPermissions}
								/>
							</>
						)}
					</>
				)
			}
		/>
	);
}

export default InviteProvisionalModal;
